import React, { useEffect, useMemo, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Avatar, CardHeader, IconButton, Tooltip, Popover, List, ListItem, ListItemIcon, ListItemText, Checkbox, TextField, Divider } from "@material-ui/core";
import { Chip } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { FormatMask } from "../../utils/FormatMask";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import AddIcon from "@material-ui/icons/Add";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TicketDetailsModal from "../TicketDetailsModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
	kanbanChip: {
		height: 18,
		borderRadius: 6,
		fontSize: "0.72rem",
		marginLeft: 8,
	},
	kanbanChipLabel: {
		paddingLeft: 6,
		paddingRight: 6,
		fontWeight: 400,
		letterSpacing: 0.2,
	},
	tagsRow: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 4,
		marginTop: 2,
		flex: 1,
		minWidth: 0,
		whiteSpace: "normal",
		overflow: "visible",
	},
	tagChip: {
		height: 18,
		borderRadius: 6,
		fontSize: "0.72rem",
		flexShrink: 0,
	},
	tagChipLabel: {
		paddingLeft: 6,
		paddingRight: 6,
		fontWeight: 400,
		letterSpacing: 0.2,
	},
	thirdRow: {
		display: "flex",
		alignItems: "center",
		flexWrap: "nowrap",
		gap: 8,
		marginTop: 2,
		width: "100%",
		// sem wrap também no mobile; manter tudo em uma linha
	},
	kanbanRow: {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		whiteSpace: "nowrap",
		flexShrink: 0,
	},
	separator: {
		opacity: 0.6,
		margin: "0 6px",
		flexShrink: 0,
	},
}));

const TicketInfo = ({ contact, ticket, onClick }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("xs"));
	const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
	const isDark = (theme.palette?.type === 'dark') || (theme.palette?.mode === 'dark');
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const { get: getCompanySetting } = useCompanySettings();

	// Flags para 'ticket avançado'
	const [manualTicketCreationEnabled, setManualTicketCreationEnabled] = useState(false);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const planAdvanced = useMemo(
		() => (user?.company?.plan?.useKanbanAdvanced || user?.plan?.useKanbanAdvanced || user?.useKanbanAdvanced || false),
		[user]
	);
	const advancedTicketsEnabled = useMemo(
		() => Boolean(planAdvanced) && (settingsLoaded ? Boolean(manualTicketCreationEnabled) : false),
		[planAdvanced, manualTicketCreationEnabled, settingsLoaded]
	);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const setting = await getCompanySetting({ column: "manualTicketCreation" });
				if (!mounted) return;
				setManualTicketCreationEnabled(String(setting?.manualTicketCreation || "disabled") === "enabled");
			} catch (e) {
				if (!mounted) return;
				setManualTicketCreationEnabled(false);
			} finally {
				if (mounted) setSettingsLoaded(true);
			}
		})();
		return () => { mounted = false; };
	}, [getCompanySetting]);

	const [amount, setAmount] = useState("");
	const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
	const [allTags, setAllTags] = useState([]);
	const [selectedTags, setSelectedTags] = useState([]);
	const [kanbanAnchorEl, setKanbanAnchorEl] = useState(null);
	const [kanbanOptions, setKanbanOptions] = useState([]);
	const [kanbanTagLocal, setKanbanTagLocal] = useState(null);
	const [newTagName, setNewTagName] = useState("");
	const [creatingTag, setCreatingTag] = useState(false);
	const [officialTickets, setOfficialTickets] = useState([]);
	const [loadingOfficial, setLoadingOfficial] = useState(false);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [detailsTicketId, setDetailsTicketId] = useState(null);
	const [officialsReloadKey, setOfficialsReloadKey] = useState(0);
    const [createOpen, setCreateOpen] = useState(false);

	// Atualiza chips "Tickets abertos" quando o ticket oficial é criado/salvo nesta conversa
	useEffect(() => {
		const onRefreshOfficials = (e) => {
			const det = e && e.detail ? e.detail : {};
			const sameContact = det.contactId && contact?.id && Number(det.contactId) === Number(contact.id);
			const sameSession = det.sessionId && ticket?.id && Number(det.sessionId) === Number(ticket.id);
			if (sameContact || sameSession) {
				setOfficialsReloadKey((k) => k + 1);
			}
		};
		try { window.addEventListener('refresh-officials', onRefreshOfficials); } catch {}
		return () => { try { window.removeEventListener('refresh-officials', onRefreshOfficials); } catch {} };
	}, [contact?.id, ticket?.id]);

	useEffect(() => {
		// Sincroniza visual com tags do contato quando contato muda
		if (Array.isArray(contact?.tags)) {
			setSelectedTags(contact.tags);
		} else {
			setSelectedTags([]);
		}
	}, [contact]);

	useEffect(() => {
		// Sincroniza etapa Kanban local quando ticket muda
		const t = Array.isArray(ticket?.tags) && ticket.tags.length > 0 ? ticket.tags[0] : null;
		setKanbanTagLocal(t);
	}, [ticket?.tags]);

	// Carrega tickets oficiais desta sessão quando for sessão (isOfficial=false)
	useEffect(() => {
		const loadOfficials = async () => {
			try {
				// Apenas para sessões (tickets não-oficiais)
				if (!ticket?.id || ticket?.isOfficial) { setOfficialTickets([]); return; }
				setLoadingOfficial(true);

				// 1) Tickets oficiais diretamente vinculados à sessão
				let sessionLinked = [];
				try {
					const { data } = await api.get(`/tickets`, { params: { createdFromSessionId: String(ticket.id), pageNumber: 1 } });
					sessionLinked = Array.isArray(data?.tickets) ? data.tickets : [];
				} catch {}

				// 2) Tickets oficiais em aberto do contato (open/group)
				let contactOfficials = [];
								if (contact?.id) {
					try {
						const rep = await api.get(`/ticketreport/reports`, {
							params: {
								contactId: contact.id,
												status: JSON.stringify(["open", "group", "pending"]),
								page: 1,
								pageSize: 20
							}
						});
						const reportList = Array.isArray(rep?.data?.tickets) ? rep.data.tickets : [];
						contactOfficials = reportList.filter(t => t?.isOfficial === true);
					} catch {}
				}

				// 3) Mesclar (prioriza vinculados à sessão). Não filtrar por status aqui,
				// pois o relatório retorna status traduzido ("ABERTO"/"GRUPO").
				const map = new Map();
				[...sessionLinked, ...contactOfficials]
					.filter(Boolean)
					.forEach(t => { if (!map.has(t.id)) map.set(t.id, t); });
				const merged = Array.from(map.values());

				setOfficialTickets(merged);
			} catch (e) {
				setOfficialTickets([]);
			} finally {
				setLoadingOfficial(false);
			}
		};
		loadOfficials();
	}, [ticket?.id, ticket?.isOfficial, contact?.id, officialsReloadKey]);

	const loadAllTags = async () => {
		try {
			const { data } = await api.get(`/tags/list`, { params: { kanban: 0 } });
			// A API pode retornar { tags: [...] } ou array direto conforme outras telas; normalizar
			const list = Array.isArray(data?.tags) ? data.tags : Array.isArray(data) ? data : [];
			setAllTags(list);
		} catch (err) {
			// silencioso para não poluir o header
		}
	};


	const handleOpenTags = async (e) => {
		if (e && typeof e.stopPropagation === "function") e.stopPropagation();
		if (e && typeof e.preventDefault === "function") e.preventDefault();
		setTagsAnchorEl(e.currentTarget);
		if (allTags.length === 0) {
			await loadAllTags();
		}
	};

	const handleCloseTags = () => setTagsAnchorEl(null);

	const isTagSelected = (id) => selectedTags?.some((t) => t.id === id);

	const handleToggleTag = async (tag) => {
		let updated;
		if (isTagSelected(tag.id)) {
			updated = selectedTags.filter((t) => t.id !== tag.id);
		} else {
			updated = [...selectedTags, tag];
		}
		setSelectedTags(updated);
		try {
			await api.post(`/tags/sync`, { contactId: contact.id, tags: updated });
		} catch (err) {
			// rollback simples
			setSelectedTags(selectedTags);
		}
	};

	const getRandomHexColor = () => {
		const red = Math.floor(Math.random() * 256);
		const green = Math.floor(Math.random() * 256);
		const blue = Math.floor(Math.random() * 256);
		return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
	};

	const handleCreateTag = async () => {
		const name = (newTagName || "").trim();
		if (name.length < 3) {
			toastError("Tag muito curta!");
			return;
		}
		try {
			setCreatingTag(true);
			const { data: created } = await api.post(`/tags`, { name, kanban: 0, color: getRandomHexColor() });
			// Atualiza listas locais
			const updatedAll = [...allTags, created];
			setAllTags(updatedAll);
			const updatedSelected = [...selectedTags, created];
			setSelectedTags(updatedSelected);
			await api.post(`/tags/sync`, { contactId: contact.id, tags: updatedSelected });
			setNewTagName("");
		} catch (err) {
			toastError(err);
		} finally {
			setCreatingTag(false);
		}
	};

	const visibleTags = useMemo(() => {
		const arr = Array.isArray(selectedTags) ? selectedTags : [];
		let limit = 7;
		if (isSmDown) limit = 5;
		if (isXs) limit = 3;
		return arr.slice(0, limit);
	}, [selectedTags, isSmDown, isXs]);

	const hiddenCount = useMemo(() => {
		const total = Array.isArray(selectedTags) ? selectedTags.length : 0;
		let limit = 7;
		if (isSmDown) limit = 5;
		if (isXs) limit = 3;
		return total > limit ? total - limit : 0;
	}, [selectedTags, isSmDown, isXs]);

	const loadKanbanOptions = async () => {
		try {
			const { data } = await api.get(`/tags/list`, { params: { kanban: 1 } });
			const list = Array.isArray(data?.tags) ? data.tags : Array.isArray(data) ? data : [];
			setKanbanOptions(list);
		} catch (err) {
			// silencioso
		}
	};

	const handleOpenKanban = async (e) => {
		if (e && typeof e.stopPropagation === "function") e.stopPropagation();
		if (e && typeof e.preventDefault === "function") e.preventDefault();
		setKanbanAnchorEl(e.currentTarget);
		if (kanbanOptions.length === 0) {
			await loadKanbanOptions();
		}
	};

	const handleCloseKanban = () => setKanbanAnchorEl(null);

	const handleSelectKanban = async (opt) => {
		try {
			// remove etapa atual
			await api.delete(`/ticket-tags/${ticket.id}`);
			if (opt) {
				await api.put(`/ticket-tags/${ticket.id}/${opt.id}`);
			}
			setKanbanTagLocal(opt || null);
			setKanbanAnchorEl(null);
		} catch (err) {
			// silencioso
		}
	};

	const renderCardReader = () => {
		const contactName = contact?.name || "(sem contato)";
	const rawPhone = contact?.number || "";
	const sanitized = rawPhone.replace("@c.us", "");
	const masker = new FormatMask();
	const contactPhone = sanitized ? (masker.setPhoneFormatMask(sanitized) || sanitized) : "";
		const ticketId = ticket?.id;
		const assignedUser = ticket?.user?.name || "—";

		// Etapa Kanban atual (estado local)
		const kanbanTag = kanbanTagLocal;

		const isOfficial = !!ticket?.isOfficial;
		const subject = ticket?.subject || "";
		const avatarSrc = contact?.urlPicture || contact?.profilePicUrl || "/nopicture.png";

		const handleCopyPhone = async (event) => {
			try {
				if (event && typeof event.stopPropagation === "function") event.stopPropagation();
				if (!sanitized) return;
				const digits = String(sanitized);
				if (navigator?.clipboard?.writeText) {
					await navigator.clipboard.writeText(digits);
					return;
				}
				const input = document.createElement("textarea");
				input.value = digits;
				input.setAttribute("readonly", "");
				input.style.position = "absolute";
				input.style.left = "-9999px";
				document.body.appendChild(input);
				input.select();
				document.execCommand("copy");
				document.body.removeChild(input);
			} catch (err) {
				// silencioso
			}
		};

		return (
			<CardHeader
				onClick={onClick}
				style={{ cursor: "pointer", paddingTop: 6, paddingBottom: 0 }}
				titleTypographyProps={{ noWrap: true, style: { lineHeight: 1.2, fontSize: '0.95rem' } }}
				subheaderTypographyProps={{ noWrap: false, style: { lineHeight: 1.2 } }}
				avatar={<Avatar src={avatarSrc} alt="contact_image" style={{ width: 32, height: 32 }} />}
				title={
					<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							<span>{contactName}</span>
							{contactPhone && (
								<>
									<span>•</span>
									<span>{contactPhone}</span>
									<Tooltip title="Copiar número">
										<IconButton
											size="small"
											onClick={handleCopyPhone}
											onMouseDown={(e) => e.stopPropagation()}
										>
											<FileCopyOutlinedIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</>
							)}
						</span>
						{subject && (
							<Chip
								size="small"
								label={subject}
								className={classes.tagChip}
								style={{ backgroundColor: '#e0e0e0', color: '#333' }}
							/>
						)}
						<Chip
							size="small"
							label={isOfficial ? 'TICKET OFICIAL' : 'SESSÃO'}
							className={classes.tagChip}
							style={{ backgroundColor: isOfficial ? '#2e7d32' : '#757575', color: '#fff' }}
						/>
					</span>
				}
				subheader={
					<div>
						{/* Linha 2: apenas texto "Ticket ... atribuído a ..." */}
						<span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap" }}>
							{`Ticket ${ticketId} atribuído a ${assignedUser}`}
						</span>

						{/* Linha 3: Ícone Kanban + etapa | Ícone Tags + chips */}
						<div className={classes.thirdRow}>
							{!advancedTicketsEnabled && (
								<span
									className={classes.kanbanRow}
									onClick={handleOpenKanban}
									onMouseDown={(e) => e.stopPropagation()}
									style={{ cursor: "pointer" }}
								>
									<Tooltip title="Alterar etapa do Kanban">
										<IconButton size="small" onClick={handleOpenKanban} onMouseDown={(e) => e.stopPropagation()}>
											<ViewColumnIcon fontSize="small" />
										</IconButton>
									</Tooltip>
									{(() => {
										const subj = (subject && String(subject).trim()) ? String(subject).trim() : "Sem assunto.";
										const etapa = kanbanTag?.name ? String(kanbanTag.name) : "Nenhum Kanban";
										const tip = `${subj} etapa "${etapa}"`;
										return (
											<Tooltip title={tip} arrow>
												<Chip
													className={classes.kanbanChip}
													classes={{ label: classes.kanbanChipLabel }}
													size="small"
													label={(kanbanTag?.name ? kanbanTag.name : "Nenhum Kanban").toUpperCase()}
													style={{
														backgroundColor: kanbanTag?.color || "#7c7c7c",
														color: "#fff",
													}}
												/>
											</Tooltip>
										);
									})()}
								</span>
							)}

							{!advancedTicketsEnabled && (
								<span className={classes.separator}>|</span>
							)}

							<span
								className={classes.tagsRow}
								onClick={handleOpenTags}
								onMouseDown={(e) => e.stopPropagation()}
								style={{ cursor: "pointer" }}
							>
								<Tooltip title="Gerenciar tags">
									<IconButton size="small" onClick={handleOpenTags} onMouseDown={(e) => e.stopPropagation()}>
										<LocalOfferIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								{(visibleTags.length === 0) && (
									<Chip
										className={classes.tagChip}
										classes={{ label: classes.tagChipLabel }}
										size="small"
										label={"NENHUMA TAG"}
										style={{ backgroundColor: "#bdbdbd", color: "#fff" }}
									/>
								)}
								{visibleTags.map((tag) => (
									<Chip
										key={`contact-tag-${tag.id}`}
										className={classes.tagChip}
										classes={{ label: classes.tagChipLabel }}
										size="small"
										label={tag.name ? tag.name.toUpperCase() : ""}
										style={{ backgroundColor: tag.color || "#7c7c7c", color: "#fff" }}
									/>
								))}
								{hiddenCount > 0 && (
									<Chip
										onClick={handleOpenTags}
										className={classes.tagChip}
										classes={{ label: classes.tagChipLabel }}
										size="small"
										label={`+${hiddenCount}`}
										style={{ backgroundColor: "#bdbdbd", color: "#fff", cursor: "pointer" }}
									/>
								)}
							</span>
						</div>

						{advancedTicketsEnabled && !isOfficial && (
							<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 6 }}>
								{/* Botão criar ticket oficial a partir da sessão */}
								<Tooltip title="Criar ticket oficial">
									<IconButton size="small" onClick={(e) => { e.stopPropagation(); setCreateOpen(true); }} onMouseDown={(e) => e.stopPropagation()}>
										<AddIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								{/* Ícone rótulo Tickets abertos */}
								<Tooltip title="Tickets abertos">
									<IconButton
										size="small"
										disableRipple
										onMouseDown={(e) => e.stopPropagation()}
										onClick={(e) => { e.stopPropagation(); setCreateOpen(true); }}
										style={{ color: isDark ? theme.palette.grey[300] : theme.palette.grey[700] }}
									>
										<ConfirmationNumberIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								{/* Chips dos tickets oficiais em aberto dessa conversa/contato */}
								{officialTickets.map((t) => {
									const titleText = (t && t.subject && String(t.subject).trim()) ? String(t.subject) : "Sem assunto.";
									return (
										<Tooltip key={`official-${t.id}`} title={titleText} arrow>
																<Chip
												className={classes.tagChip}
												size="small"
												label={`#${t.id}`}
												style={{ backgroundColor: '#1976d2', color: '#fff', cursor: 'pointer' }}
												onClick={(e) => {
													e.stopPropagation();
													setDetailsTicketId(t.id);
													setDetailsOpen(true);
												}}
											/>
										</Tooltip>
									);
								})}
							</div>
						)}
					</div>
				}

			/>
		);
	}

	const handleChange = (event) => {
		const value = event.target.value;

		setAmount(value);
	}


	return (
		<React.Fragment>
			{renderCardReader()}

			{/* Modal de detalhes do ticket oficial aberto a partir dos chips */}
			<TicketDetailsModal
				open={detailsOpen}
				ticketId={detailsTicketId}
				onClose={() => setDetailsOpen(false)}
				onSaved={() => {
					setDetailsOpen(false);
					setOfficialsReloadKey((k) => k + 1);
				}}
			/>

			{/* Modal de criação de ticket oficial (a partir do botão +) */}
			<TicketDetailsModal
				open={createOpen}
				ticketId={null}
				initialContact={contact}
				lockContact={true}
				onClose={() => setCreateOpen(false)}
				onSaved={() => {
					setCreateOpen(false);
					setOfficialsReloadKey((k) => k + 1);
					// opcional: disparar evento para sincronizar outros componentes
					try { window.dispatchEvent(new CustomEvent('refresh-officials', { detail: { contactId: contact?.id, sessionId: ticket?.id } })); } catch {}
				}}
			/>

			<Popover
				open={Boolean(tagsAnchorEl)}
				anchorEl={tagsAnchorEl}
				onClose={handleCloseTags}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<div style={{ minWidth: 260, maxWidth: 360 }}>
					<div style={{ padding: 12, display: "flex", gap: 8 }} onMouseDown={(e) => e.stopPropagation()}>
						<TextField
							value={newTagName}
							onChange={(e) => setNewTagName(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); }
							}}
							variant="outlined"
							size="small"
							placeholder="Nova tag"
							fullWidth
						/>
						<IconButton color="primary" onClick={handleCreateTag} disabled={creatingTag}>
							<AddIcon />
						</IconButton>
					</div>
					<Divider />
					<List dense style={{ maxHeight: 300, overflowY: "auto" }}>
						{allTags.length === 0 && (
							<ListItem>
								<ListItemText primary="Nenhuma tag cadastrada" />
							</ListItem>
						)}
						{allTags.map((tag) => (
							<ListItem key={`tag-option-${tag.id}`} button onClick={() => handleToggleTag(tag)}>
								<ListItemIcon>
									<Checkbox edge="start" checked={isTagSelected(tag.id)} tabIndex={-1} disableRipple />
								</ListItemIcon>
								<ListItemText primary={tag.name} />
							</ListItem>
						))}
					</List>
				</div>
			</Popover>

			{/* Popover Kanban */}
			<Popover
				open={Boolean(kanbanAnchorEl)}
				anchorEl={kanbanAnchorEl}
				onClose={handleCloseKanban}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<List dense style={{ maxHeight: 300, overflowY: "auto", minWidth: 240 }}>
					<ListItem button onClick={() => handleSelectKanban(null)}>
						<ListItemText primary="Nenhum Kanban" />
					</ListItem>
					{kanbanOptions.map((opt) => (
						<ListItem key={`kanban-opt-${opt.id}`} button onClick={() => handleSelectKanban(opt)}>
							<ListItemText primary={opt.name} />
						</ListItem>
					))}
				</List>
			</Popover>
		</React.Fragment>
	);
};

export default TicketInfo;
